import type { ReactNode } from 'react';

import {
  faCheck,
  faSparkles,
  faWaveformLines,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export type NoteGenerationStep =
  | 'preparing'
  | 'uploading'
  | 'generating'
  | 'complete';

const STEPS = [
  {
    key: 'preparing' as const,
    text: 'Preparing audio...',
    icon: faWaveformLines,
  },
  {
    key: 'uploading' as const,
    text: 'Uploading recording...',
    icon: faWaveformLines,
  },
  {
    key: 'generating' as const,
    text: 'Generating your note...',
    icon: faSparkles,
  },
  { key: 'complete' as const, text: 'Complete!', icon: faCheck },
] as const;

const DEFAULT_SUBTITLE_GENERATING = 'AI is analyzing your recording...';
const DEFAULT_SUBTITLE_OTHER = 'This will only take a moment';

export interface NoteGenerationLoadingAnimationProps {
  currentStep: NoteGenerationStep;
  /** Optional overrides for step labels (e.g. <Trans> for i18n). Keys: preparing, uploading, generating, complete. */
  stepLabels?: Partial<Record<NoteGenerationStep, ReactNode>>;
  /** Optional subtitle override. If not set, uses default: "AI is analyzing..." when generating, else "This will only take a moment". */
  subtitle?: ReactNode;
  /** Optional overrides for the three progress labels under the dots. Default: Prepare, Upload, Generate. */
  progressLabels?: {
    prepare?: ReactNode;
    upload?: ReactNode;
    generate?: ReactNode;
  };
}

export const NoteGenerationLoadingAnimation = ({
  currentStep,
  stepLabels,
  subtitle,
  progressLabels,
}: NoteGenerationLoadingAnimationProps) => {
  const currentIndex = Math.max(
    0,
    STEPS.findIndex((s) => s.key === currentStep),
  );
  const step = STEPS[currentIndex] ?? STEPS[0];
  const text = stepLabels?.[step.key] ?? step.text;
  const icon = step.icon;

  const subtitleText =
    subtitle ??
    (currentStep === 'generating'
      ? DEFAULT_SUBTITLE_GENERATING
      : DEFAULT_SUBTITLE_OTHER);

  return (
    <div
      className="vai-flex vai-flex-col vai-items-center vai-justify-center vai-h-full vai-w-full vai-p-6"
      data-testid="loading-animation"
    >
      {/* Animated icon container */}
      <div className="vai-relative vai-mb-6">
        {/* Outer spinning ring */}
        <div className="vai-absolute vai-inset-0 vai-w-24 vai-h-24 vai-rounded-full vai-border-4 vai-border-blue-100 vai-animate-spin vai-border-t-blue-500" />

        {/* Inner pulsing circle with icon */}
        <div className="vai-w-24 vai-h-24 vai-rounded-full vai-bg-gradient-to-br vai-from-blue-50 vai-to-blue-100 vai-flex vai-items-center vai-justify-center vai-animate-pulse">
          <FontAwesomeIcon
            icon={icon}
            className="vai-w-9 vai-h-9 vai-text-blue-600"
          />
        </div>

        {/* Floating sparkles */}
        <div className="vai-absolute vai--top-2 vai--right-2 vai-animate-bounce">
          <FontAwesomeIcon
            icon={faSparkles}
            className="vai-w-4 vai-h-4 vai-text-amber-400"
          />
        </div>
        <div
          className="vai-absolute vai--bottom-1 vai--left-1 vai-animate-bounce"
          style={{ animationDelay: '0.5s' }}
        >
          <FontAwesomeIcon
            icon={faSparkles}
            className="vai-w-3 vai-h-3 vai-text-blue-400"
          />
        </div>
      </div>

      {/* Message */}
      <h2
        className="vai-text-lg vai-font-semibold vai-text-slate-800 vai-text-center vai-mb-2 vai-transition-all vai-duration-300"
        data-testid="loading-message"
      >
        {text}
      </h2>

      <p className="vai-text-sm vai-text-slate-500 vai-text-center">
        {subtitleText}
      </p>

      {/* Progress steps */}
      <div className="vai-flex vai-items-center vai-gap-2 vai-mt-5">
        {STEPS.slice(0, 3).map((s, index) => {
          const isActive = index === currentIndex;
          const isComplete = index < currentIndex;
          return (
            <div key={s.key} className="vai-flex vai-items-center">
              {/* Step indicator */}
              <div
                className={`vai-w-8 vai-h-8 vai-rounded-full vai-flex vai-items-center vai-justify-center vai-transition-all vai-duration-300 vai-text-xs vai-font-medium ${
                  isComplete
                    ? 'vai-bg-green-500 vai-text-white'
                    : isActive
                      ? 'vai-bg-blue-500 vai-text-white vai-scale-110'
                      : 'vai-bg-slate-200 vai-text-slate-500'
                }`}
              >
                {isComplete ? (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="vai-w-3.5 vai-h-3.5"
                  />
                ) : (
                  index + 1
                )}
              </div>
              {/* Connector line */}
              {index < 2 && (
                <div
                  className={`vai-w-8 vai-h-0.5 vai-transition-colors vai-duration-300 ${
                    isComplete ? 'vai-bg-green-500' : 'vai-bg-slate-200'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Step labels */}
      <div className="vai-flex vai-justify-between vai-w-full vai-max-w-[200px] vai-mt-2">
        <span className="vai-text-[10px] vai-text-slate-500">
          {progressLabels?.prepare ?? 'Prepare'}
        </span>
        <span className="vai-text-[10px] vai-text-slate-500">
          {progressLabels?.upload ?? 'Upload'}
        </span>
        <span className="vai-text-[10px] vai-text-slate-500">
          {progressLabels?.generate ?? 'Generate'}
        </span>
      </div>
    </div>
  );
};
